import React, { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Drawer from '~/components/Drawer'
import { FormFields } from '~/components/FormFields'
import { DrawerEl } from '~/components/Drawer/style'
import { GlobalEl } from '~/styles/app'
import { handleErrors } from '~/utils/helpers'
import MilkMoneyAccountingService from '~/api/services/milkMoneyAccounting'
import { useDebounce } from 'use-lodash-debounce';

export interface BankAccount {
  id?: number | null
  name: string
  iban: string
  swift: string
  status: 0 | 1
}

interface Props {
  isVisible: boolean
  account: BankAccount | null
  onClose: () => void
  onSave: (data: BankAccount) => void
}

const BankAccountDrawer: React.FC<Props> = (props) => {
  const { isVisible, account, onClose, onSave } = props
  const { t } = useTranslation()

  const isEdit = Boolean(account?.id)

  const [loadingBankInfo, setLoadingBankInfo] = useState(false)

  const { control, handleSubmit, reset, setValue, formState: { touchedFields, dirtyFields } } = useForm<BankAccount>({
    mode: 'onSubmit',
    defaultValues: {
      id: null,
      name: '',
      iban: '',
      swift: '',
      status: 1
    }
  })
  
  const watchIban = useWatch({ control, name: 'iban' })
  const debounceIban = useDebounce(watchIban, 300)

  useEffect(() => {
    if (account) {
      reset(account)
    } else {
      reset({
        id: null,
        name: '',
        iban: '',
        swift: '',
        status: 1
      })
    }
  }, [account])

  useEffect(() => {
    if(isVisible && (touchedFields.iban || dirtyFields.iban)) {
      fetchBankInfo()
    }
  }, [debounceIban, isVisible, touchedFields, dirtyFields])

  function onSubmit(data: BankAccount) {
    onSave(data)
    reset()
  }

  function handleClose() {
    reset()
    onClose()
  }

  async function fetchBankInfo() {
    setLoadingBankInfo(true)
    try {
      const response = await MilkMoneyAccountingService.checkBankIban({ iban: watchIban })
      if (response.data.ibanValid) {
        setValue('name', response.data.name)
        setValue('swift', response.data.swift)
      }
    } catch (error) {
      handleErrors(error)
    }
    setLoadingBankInfo(false)
  }

  function renderContent() {
    return (
      <>
        <FormFields.Input
          control={control}
          name='iban'
          label={t('IBAN')}
          id={'iban'}
          loading={loadingBankInfo}
          placeholder={t('Please enter IBAN')}
          rules={{
            required: t('IBAN is required'),
            maxLength: {
              value: 100,
              message: t('Maximum 100 characters')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name='name'
          label={t('Bank name')}
          placeholder={t('Please enter bank name')}
          rules={{
            required: t('Bank name is required'),
            maxLength: {
              value: 100,
              message: t('Maximum 100 characters')
            }
          }}
        />
        <FormFields.Input
          control={control}
          name='swift'
          label={t('SWIFT/BIC')}
          placeholder={t('Please enter SWIFT/BIC')}
          rules={{
            required: t('SWIFT/BIC is required'),
            maxLength: {
              value: 100,
              message: t('Maximum 100 characters')
            }
          }}
        />
        <FormFields.Checkbox
          control={control}
          name='status'
          label={t('Active')}
          checkboxValue={1}
          defaultValue={0}
        />
        <DrawerEl.DrawerFooter>
          <GlobalEl.PrimaryButton type='button' onClick={handleSubmit(onSubmit)}>
            {isEdit ? t('Update') : t('Add')}
          </GlobalEl.PrimaryButton>
        </DrawerEl.DrawerFooter>
      </>
    )
  }

  return (
    <Drawer
      onHide={handleClose}
      header={isEdit ? t('Edit bank account') : t('Add bank account')}
      content={renderContent()}
      isVisible={isVisible}
      closeOnEscape
    />
  )
}

export default BankAccountDrawer
